import React, { useState } from 'react';
import toast from 'react-hot-toast';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const MaintenanceRecords = ({
  asset,
  isEditing,
  onUpdate
}) => {

  // =========================================================
  // STATE
  // =========================================================

  const [showAddRecord, setShowAddRecord] = useState(false);

  const [newRecord, setNewRecord] = useState({
    type: '',
    description: '',
    performed_by: '',
    cost: '',
    scheduled_date: '',
    completed_date: '',
    status: 'pending',
    notes: ''
  });

  // =========================================================
  // OPTIONS
  // =========================================================

  const maintenanceTypeOptions = [
    {
      value: 'preventive',
      label: 'Preventive Maintenance'
    },
    {
      value: 'corrective',
      label: 'Corrective Maintenance'
    },
    {
      value: 'emergency',
      label: 'Emergency Repair'
    },
    {
      value: 'upgrade',
      label: 'Hardware Upgrade'
    },
    {
      value: 'software',
      label: 'Software Update'
    },
    {
      value: 'cleaning',
      label: 'Cleaning / Inspection'
    },
    {
      value: 'calibration',
      label: 'Calibration'
    },
    {
      value: 'warranty',
      label: 'Warranty Service'
    }
  ];

  const statusOptions = [
    {
      value: 'pending',
      label: 'Pending'
    },
    {
      value: 'in-progress',
      label: 'In Progress'
    },
    {
      value: 'completed',
      label: 'Completed'
    },
    {
      value: 'cancelled',
      label: 'Cancelled'
    },
    {
      value: 'on-hold',
      label: 'On Hold'
    }
  ];

  // =========================================================
  // DATABASE DATA
  // =========================================================

  /*
   * Support both naming formats:
   *
   * maintenance_records
   * maintenanceRecords
   */

  const maintenanceRecords = Array.isArray(
    asset?.maintenance_records
  )
    ? asset.maintenance_records
    : Array.isArray(asset?.maintenanceRecords)
      ? asset.maintenanceRecords
      : [];

  // =========================================================
  // FORMAT DATE
  // =========================================================

  const formatDate = (date) => {

    if (!date) {
      return 'N/A';
    }

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleDateString(
      'en-US',
      {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }
    );
  };

  // =========================================================
  // TYPE LABEL
  // =========================================================

  const getTypeLabel = (type) => {

    const found = maintenanceTypeOptions.find(
      option => option.value === type
    );

    return found
      ? found.label
      : type || 'Maintenance';
  };

  // =========================================================
  // STATUS COLOR
  // =========================================================

  const getStatusColor = (status) => {

    switch (status) {

      case 'completed':
        return 'bg-green-100 text-green-700';

      case 'pending':
        return 'bg-yellow-100 text-yellow-700';

      case 'in-progress':
        return 'bg-blue-100 text-blue-700';

      case 'cancelled':
        return 'bg-red-100 text-red-700';

      case 'on-hold':
        return 'bg-orange-100 text-orange-700';

      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // =========================================================
  // TYPE ICON
  // =========================================================

  const getTypeIcon = (type) => {

    switch (type) {

      case 'preventive':
        return 'Calendar';

      case 'corrective':
        return 'Wrench';

      case 'emergency':
        return 'AlertTriangle';

      case 'upgrade':
        return 'TrendingUp';

      case 'software':
        return 'Download';

      case 'cleaning':
        return 'Search';

      case 'calibration':
        return 'Settings';

      case 'warranty':
        return 'Shield';

      default:
        return 'Tool';
    }
  };

  // =========================================================
  // TOTAL COST
  // =========================================================

  const calculateTotalCost = () => {

    return maintenanceRecords.reduce(
      (total, item) => {

        return (
          total +
          (
            parseFloat(item?.cost) || 0
          )
        );
      },
      0
    );
  };

  // =========================================================
  // ADD RECORD
  // =========================================================

  const handleAddRecord = () => {

    if (
      !newRecord.type ||
      !newRecord.description
    ) {
      toast.error('Please select maintenance type and enter description.');
      return;
    }

    const record = {

      id: Date.now(),

      type: newRecord.type,

      description:
        newRecord.description,

      performed_by:
        newRecord.performed_by,

      cost:
        parseFloat(newRecord.cost) || 0,

      scheduled_date:
        newRecord.scheduled_date || null,

      completed_date:
        newRecord.completed_date || null,

      status:
        newRecord.status || 'pending',

      notes:
        newRecord.notes || ''
    };

    const updatedRecords = [
      ...maintenanceRecords,
      record
    ];

    /*
    |--------------------------------------------------------------------------
    | UPDATE PARENT ASSET
    |--------------------------------------------------------------------------
    */

    onUpdate({
      ...asset,

      maintenance_records:
        updatedRecords
    });

    toast.success('Maintenance record added');

    /*
    |--------------------------------------------------------------------------
    | RESET FORM
    |--------------------------------------------------------------------------
    */

    setNewRecord({
      type: '',
      description: '',
      performed_by: '',
      cost: '',
      scheduled_date: '',
      completed_date: '',
      status: 'pending',
      notes: ''
    });

    setShowAddRecord(false);
  };

  // =========================================================
  // DELETE RECORD
  // =========================================================

  const handleDeleteRecord = (recordId) => {

    const confirmed = window.confirm(
      'Are you sure you want to delete this maintenance record?'
    );

    if (!confirmed) {
      return;
    }

    const updatedRecords =
      maintenanceRecords.filter(
        record =>
          String(record.id) !==
          String(recordId)
      );

    onUpdate({
      ...asset,

      maintenance_records:
        updatedRecords
    });

    toast.success('Maintenance record removed');
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (

    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">

      {/* =====================================================
          HEADER
      ====================================================== */}

      <div className="flex items-center justify-between mb-6">

        <div>

          <h3 className="text-lg font-semibold">
            Maintenance Records
          </h3>

          <p className="text-sm text-muted-foreground mt-1">
            Maintenance and service history for this asset
          </p>

        </div>

        {isEditing && (

          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={() =>
              setShowAddRecord(
                !showAddRecord
              )
            }
          >
            Add Record
          </Button>

        )}

      </div>

      {/* =====================================================
          SUMMARY
      ====================================================== */}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">

        {/* TOTAL */}

        <div className="bg-muted rounded-lg p-4">

          <p className="text-sm text-muted-foreground">
            Total Records
          </p>

          <p className="text-2xl font-bold mt-1">
            {maintenanceRecords.length}
          </p>

        </div>

        {/* COMPLETED */}

        <div className="bg-muted rounded-lg p-4">

          <p className="text-sm text-muted-foreground">
            Completed
          </p>

          <p className="text-2xl font-bold mt-1">

            {
              maintenanceRecords.filter(
                item =>
                  item?.status ===
                  'completed'
              ).length
            }

          </p>

        </div>

        {/* COST */}

        <div className="bg-muted rounded-lg p-4">

          <p className="text-sm text-muted-foreground">
            Total Cost
          </p>

          <p className="text-2xl font-bold mt-1">

            ₹
            {
              calculateTotalCost()
                .toLocaleString(
                  'en-IN',
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }
                )
            }

          </p>

        </div>

      </div>

      {/* =====================================================
          ADD FORM
      ====================================================== */}

      {showAddRecord && isEditing && (

        <div className="bg-muted rounded-lg p-4 mb-6">

          <div className="flex items-center justify-between mb-4">

            <h4 className="font-semibold">
              Add Maintenance Record
            </h4>

            <button
              type="button"
              onClick={() =>
                setShowAddRecord(false)
              }
              className="text-muted-foreground hover:text-foreground"
            >
              ×
            </button>

          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* TYPE */}

            <Select
              label="Type"
              options={
                maintenanceTypeOptions
              }
              value={
                newRecord.type
              }
              onChange={
                value =>
                  setNewRecord({
                    ...newRecord,
                    type: value
                  })
              }
            />

            {/* STATUS */}

            <Select
              label="Status"
              options={
                statusOptions
              }
              value={
                newRecord.status
              }
              onChange={
                value =>
                  setNewRecord({
                    ...newRecord,
                    status: value
                  })
              }
            />

            {/* DESCRIPTION */}

            <Input
              label="Description"
              type="text"
              value={
                newRecord.description
              }
              onChange={
                e =>
                  setNewRecord({
                    ...newRecord,
                    description:
                      e.target.value
                  })
              }
              placeholder="Enter maintenance description"
            />

            {/* PERFORMED BY */}

            <Input
              label="Performed By"
              type="text"
              value={
                newRecord.performed_by
              }
              onChange={
                e =>
                  setNewRecord({
                    ...newRecord,
                    performed_by:
                      e.target.value
                  })
              }
              placeholder="Enter technician / team"
            />

            {/* COST */}

            <Input
              label="Cost"
              type="number"
              value={
                newRecord.cost
              }
              onChange={
                e =>
                  setNewRecord({
                    ...newRecord,
                    cost:
                      e.target.value
                  })
              }
              placeholder="0.00"
            />

            {/* SCHEDULED */}

            <Input
              label="Scheduled Date"
              type="date"
              value={
                newRecord.scheduled_date
              }
              onChange={
                e =>
                  setNewRecord({
                    ...newRecord,
                    scheduled_date:
                      e.target.value
                  })
              }
            />

            {/* COMPLETED */}

            <Input
              label="Completed Date"
              type="date"
              value={
                newRecord.completed_date
              }
              onChange={
                e =>
                  setNewRecord({
                    ...newRecord,
                    completed_date:
                      e.target.value
                  })
              }
            />

          </div>

          {/* NOTES */}

          <div className="mt-4">

            <label className="block text-sm font-medium mb-2">
              Notes
            </label>

            <textarea
              rows={3}
              value={
                newRecord.notes
              }
              onChange={
                e =>
                  setNewRecord({
                    ...newRecord,
                    notes:
                      e.target.value
                  })
              }
              placeholder="Enter additional notes..."
              className="w-full border border-border rounded-lg p-3 bg-background"
            />

          </div>

          {/* BUTTONS */}

          <div className="flex justify-end gap-2 mt-4">

            <Button
              variant="ghost"
              onClick={() =>
                setShowAddRecord(false)
              }
            >
              Cancel
            </Button>

            <Button
              variant="default"
              iconName="Save"
              iconPosition="left"
              onClick={
                handleAddRecord
              }
            >
              Add Record
            </Button>

          </div>

        </div>

      )}

      {/* =====================================================
          RECORD LIST
      ====================================================== */}

      {maintenanceRecords.length > 0 ? (

        <div className="space-y-4">

          {maintenanceRecords.map(
            (record, index) => (

              <div
                key={
                  record?.id ??
                  index
                }
                className="border border-border rounded-lg p-4"
              >

                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">

                  {/* LEFT */}

                  <div className="flex gap-3">

                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">

                      <Icon
                        name={
                          getTypeIcon(
                            record?.type
                          )
                        }
                        size={20}
                        color="currentColor"
                      />

                    </div>

                    <div>

                      <div className="flex items-center gap-2 flex-wrap">

                        <h4 className="font-semibold">

                          {
                            getTypeLabel(
                              record?.type
                            )
                          }

                        </h4>

                        <span
                          className={
                            `px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                              record?.status
                            )}`
                          }
                        >

                          {
                            record?.status
                              ? record.status
                                  .replace(
                                    '-',
                                    ' '
                                  )
                                  .replace(
                                    /\b\w/g,
                                    letter =>
                                      letter.toUpperCase()
                                  )
                              : 'Unknown'
                          }

                        </span>

                      </div>

                      <p className="text-sm text-muted-foreground mt-1">

                        {
                          record?.description ||
                          'No description'
                        }

                      </p>

                    </div>

                  </div>

                  {/* DELETE */}

                  {isEditing && (

                    <button
                      type="button"
                      onClick={() =>
                        handleDeleteRecord(
                          record?.id
                        )
                      }
                      className="text-red-500 hover:text-red-700"
                      title="Delete maintenance record"
                    >

                      <Icon
                        name="Trash2"
                        size={18}
                      />

                    </button>

                  )}

                </div>

                {/* DETAILS */}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-border">

                  <div>

                    <p className="text-xs text-muted-foreground">
                      Performed By
                    </p>

                    <p className="text-sm font-medium mt-1">

                      {
                        record?.performed_by ||
                        'N/A'
                      }

                    </p>

                  </div>

                  <div>

                    <p className="text-xs text-muted-foreground">
                      Cost
                    </p>

                    <p className="text-sm font-medium mt-1">

                      ₹
                      {
                        (
                          parseFloat(
                            record?.cost
                          ) || 0
                        ).toLocaleString(
                          'en-IN',
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }
                        )
                      }

                    </p>

                  </div>

                  <div>

                    <p className="text-xs text-muted-foreground">
                      Scheduled Date
                    </p>

                    <p className="text-sm font-medium mt-1">

                      {
                        formatDate(
                          record?.scheduled_date
                        )
                      }

                    </p>

                  </div>

                  <div>

                    <p className="text-xs text-muted-foreground">
                      Completed Date
                    </p>

                    <p className="text-sm font-medium mt-1">

                      {
                        formatDate(
                          record?.completed_date
                        )
                      }

                    </p>

                  </div>

                </div>

                {/* NOTES */}

                {record?.notes && (

                  <div className="mt-4 bg-muted rounded-lg p-3">

                    <p className="text-xs text-muted-foreground mb-1">
                      Notes
                    </p>

                    <p className="text-sm">
                      {record.notes}
                    </p>

                  </div>

                )}

              </div>

            )
          )}

        </div>

      ) : (

        /* ===================================================
           EMPTY STATE
        ==================================================== */

        <div className="flex flex-col items-center justify-center py-16 text-center">

          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">

            <Icon
              name="Wrench"
              size={30}
              color="currentColor"
            />

          </div>

          <h4 className="font-semibold text-lg">
            No maintenance records
          </h4>

          <p className="text-sm text-muted-foreground mt-2 max-w-md">

            No maintenance records have been added
            for this asset yet.

          </p>

          {isEditing && (

            <Button
              variant="outline"
              size="sm"
              iconName="Plus"
              iconPosition="left"
              className="mt-4"
              onClick={() =>
                setShowAddRecord(true)
              }
            >
              Add First Record
            </Button>

          )}

        </div>

      )}

    </div>
  );
};

export default MaintenanceRecords;